import { createApp } from 'vue';
import { RouterLink } from 'vue-router';
import { createPinia } from 'pinia';
import { init } from '@sentry/vue';
import { LINK_INJECTION_KEY } from '@exfluential/ui';

import '@fontsource-variable/inter';
import './assets/css/main.css';

import {
  IS_PROD,
  APP_VERSION,
  APP_ENVIRONMENT,
  SENTRY_DSN,
  IS_MAINTENANCE_ENABLED,
} from '@/config/common';
import App from './App.vue';
import Maintenance from '@/views/Maintenance.vue';
import router from './router';
import { useErrorStore } from '@/store/use-error-store';
import { DENIED_SENTRY_URLS, IGNORED_SENTRY_ERRORS } from '@/constants/sentry';
import { setupErrorTracking, trackError } from '@/utils/analytics';
import { PostHogPlugin } from '@/plugins/posthog';

const app = createApp(IS_MAINTENANCE_ENABLED ? Maintenance : App);

app.config.errorHandler = (err: unknown, instance, info) => {
  const { handleError } = useErrorStore();

  // Track error in PostHog
  if (err instanceof Error) {
    trackError(err.message, err.stack, instance?.$options?.name || 'unknown', {
      info,
      route: router.currentRoute.value.path,
    });
  }

  // Handle error with existing store
  handleError(router, err);
};

// Initialize PostHog error tracking
setupErrorTracking();

if (IS_PROD) {
  init({
    app,
    dsn: SENTRY_DSN,
    tracesSampleRate: 1,
    environment: APP_ENVIRONMENT,
    release: APP_VERSION,
    ignoreErrors: IGNORED_SENTRY_ERRORS,
    denyUrls: DENIED_SENTRY_URLS,
  });
}

// Shared @exfluential/ui components render their `to` targets through this.
app.provide(LINK_INJECTION_KEY, RouterLink);

app.use(createPinia()).use(router).use(PostHogPlugin).mount('#app');

// eslint-disable-next-line no-console
console.log(`Application version: ${APP_VERSION}`);
